import React, { FC, CSSProperties, useEffect, useRef, useReducer, ReactNode } from "react";
import flatpickr from "flatpickr";
import { Instance } from "flatpickr/dist/types/instance";
import moment from "moment";
import "flatpickr/dist/themes/material_green.css";


export interface ISelectDateStartProps {
  currentDates: string[] | null | undefined;
  disabled?: boolean;
  /**
   * Fires only when the picker changes the date
   * @param date number
   */
  onPickerChange?(date: string[]): void;
  containerStyle?: CSSProperties;
  children?(state: IState): ReactNode;
  id?: string;
  dates?: { [date: string]: boolean };
}

interface IState {
  currentDate: string | null;
  dates: string[] | null;
  visible: boolean;
  previousAction?: IAction;
}

enum ActionTypes {
  SET_PICKER_DATES = "SET_PICKER_DATE",
  SET_PROPS_DATES = "SET_PROPS_DATE",
  SET_VISIBILITY = "SET_VISIBILITY"
}

interface IAction<T = any> {
  type: ActionTypes;
  payload?: T;
}

const getInitialState = (): IState => ({
  dates: null,
  currentDate: null,
  visible: false,
});

const getDateFromSelectedDates = (date: Date) => {
  let _date: Date;
  if (date == null) {
    _date = moment().toDate();
  } else {
    _date = date;
  }
  return _date
}

const mainReducer = (state: IState, action: IAction): IState => {
  switch (action.type) {

    case ActionTypes.SET_PICKER_DATES: {
      const dates = action.payload as Date[];
      let _dates: string[] = [];

      dates.forEach((date) => {
        const d = getDateFromSelectedDates(date);
        const _date = moment(d).format("YYYY.MM.DD")
        _dates.push(_date);
      })

      return ({
        ...state,
        dates: _dates,
      });
    }

    case ActionTypes.SET_PROPS_DATES: {
      let dates: string[] = action.payload;

      return ({
        ...state,
        dates,
      });
    }

    case ActionTypes.SET_VISIBILITY: {
      const visible = action.payload as boolean;
      return ({
        ...state,
        visible,
      });
    }

    default:
      break;
  }

  return state;
}

const reducer = (state: IState, action: IAction): IState => {
  return ({
    ...mainReducer(state, action),
    previousAction: action,
  });
}

export const SelectDateStart: FC<ISelectDateStartProps> = props => {
  const [state, dispatch] = useReducer(reducer, getInitialState())

  const fp = useRef<Instance>(null);
  const elem = useRef<HTMLInputElement>(null);

  // on props change, the instance should reflect that
  useEffect(() => {
    if (props.currentDates == null) return;

    dispatch({ type: ActionTypes.SET_PROPS_DATES, payload: props.currentDates });
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    fp.current != null
      ? fp.current.setDate(
        props.currentDates.map(date => moment(date, "YYYY.MM.DD").toDate())
      )
      : null;
  }, [props.currentDates])

  // destroy picker on state.visible === false
  useEffect(() => {
    if (state.visible) return;
    if (fp.current == null) return;
    destroy();
  }, [state.visible])

  const onWheel = useRef(() => {
    dispatch({ type: ActionTypes.SET_VISIBILITY, payload: false });
  });

  const destroy = () => {
    document.removeEventListener("wheel", onWheel.current);
    if (fp.current == null) return;
    fp.current.close();
    fp.current.destroy();
    (fp.current as any) = null;
  };

  useEffect(() => {
    return () => {
      destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDayCreate = (dObj: any, dStr: any, fp: any, dayElem: HTMLElement & any) => {
    dayElem.innerHTML = `<b>${dayElem.innerHTML}</b>`
  }

  useEffect(() => {
    if (fp.current == null) return;
    fp.current.set("onDayCreate", onDayCreate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.currentDates])

  const create = () => {
    if (elem.current == null) return;
    if (fp.current != null) return;
    (fp.current as any) = flatpickr(elem.current, {
      onChange(selectedDates, dateStr, instance) {
        dispatch({ type: ActionTypes.SET_PICKER_DATES, payload: selectedDates });

        props.onPickerChange?.(selectedDates.map(date => moment(date).format("YYYY.MM.DD")));
      },
      onReady(selectedDate, dateStr, instance) {
        instance.open();
      },
      onOpen(selectedDate, dateStr, instance) {
        dispatch({ type: ActionTypes.SET_VISIBILITY, payload: true });
      },
      onClose(selectedDate, dateStr, instance) {
        dispatch({ type: ActionTypes.SET_VISIBILITY, payload: false });
      },
      disableMobile: true,
      showMonths: 1,
      dateFormat: "d m Y",
      mode: "range",
      minDate: (moment.utc(moment().startOf('month').format("YYYY-MM-DD")).unix()) * 1000,
      maxDate: ((moment.utc(moment().startOf('month').add(12, 'months').format("YYYY-MM-DD")).unix()) * 1000),
    });
    document.addEventListener("wheel", onWheel.current);
    if (props.currentDates != null) {
      fp.current!.setDate(
        props.currentDates.map(date => moment(date, "YYYY.MM.DD").toDate())
      );
    }
  };

  return (
    <div ref={elem} onClick={create} className="Select_Start_Date" id={props?.id}
    >
      Select Dates: {props.currentDates?.length === 2
        ? <>
          {moment(props?.currentDates?.[0] as string, "YYYY.MM.DD").format('MM/DD/YYYY')} - {moment(props?.currentDates?.[1] as string, "YYYY.MM.DD").format('MM/DD/YYYY')}
        </>
        : null
      }
    </div>
  );
}